import DropdownSelector from 'components/DropdownSelector';
import {
  selectLinkingGroupId,
  selectSelectedDeviceId,
} from 'features/integrations/state/selectors/deviceSelectors';
import {
  selectHistoryDataLoading,
  selectInitialHistoryLoaded,
  selectIntegrationFetching,
} from 'features/integrations/state/selectors/integrationSelectors';
import { setSelectedDeviceId } from 'features/integrations/state/slices/deviceSlice';
import { DeviceIsSystemType } from 'features/integrations/utils/deviceComparisionUtils';
import { startTransition, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { safeJSONParse } from 'utils/parseUtils';
import ConnectedDevicesLoadingState from '../../components/ConnectedDevicesLoadingState';
import AwareDeviceChart from './components/AwareDeviceChart';
import DropdownSelect from './components/DropdownSelect';
import USGSDeviceGraph from './components/USGSDeviceGraph';
import {
  getAwareDevices,
  getFilterDropdownOptions,
  resolveGroupDeviceMap,
} from './utils/deviceGraphUtils';
import { getUSGSDevices } from './utils/usgsDeviceGraphUtils';

const DeviceGraph = ({
  title,
  startTime,
  endTime,
  setShowCurrentDevice,
  onSelectedDeviceIndex,
  setShowProvisional,
  widget = false,
}) => {
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery(
    startTime != null && endTime != null ? { startTime, endTime } : undefined,
  );
  const dispatch = useDispatch();

  const floodDeviceIds = useMemo(() => {
    const aware = getAwareDevices(connectedDevices)
      .map((device) => String(device.id))
      .filter(Boolean);
    const usgs = getUSGSDevices(connectedDevices)
      .map((device) => String(device.id))
      .filter(Boolean);
    const all = [...aware, ...usgs];
    return Array.from(new Set(all));
  }, [connectedDevices]);

  const [unitType, setUnitType] = useState(false);

  const [selectedDeviceMap, setSelectedDeviceMap] = useState(
    safeJSONParse(
      sessionStorage.getItem('selectedDeviceMap'),
      floodDeviceIds[0] ? { [String(floodDeviceIds[0])]: true } : {},
    ),
  );

  const [selectedDataType, setSelectedDataType] = useState(
    safeJSONParse(sessionStorage.getItem('selectedDataType'), 'sensordepths'),
  );

  const [filterDropdownOptionSelected, setFilterDropdownOptionSelected] = useState(
    safeJSONParse(sessionStorage.getItem('filterDropdownOptionSelected'), 0),
  );
  const deferredSelectedDeviceMap = useDeferredValue(selectedDeviceMap);
  const deferredSelectedDataType = useDeferredValue(selectedDataType);

  const selectedDeviceIds = Object.keys(selectedDeviceMap || {}).filter(
    (a) => selectedDeviceMap[a],
  );
  const deferredSelectedDeviceIds = Object.keys(deferredSelectedDeviceMap || {}).filter(
    (a) => deferredSelectedDeviceMap[a],
  );
  const selectedDevices = (connectedDevices || []).filter((d) =>
    deferredSelectedDeviceIds.includes(String(d.id)),
  );

  const hasUSGSSelected = selectedDevices.some(
    (d) => d.system_type === 'USGS' && d.device_type === 'Monitoring Station',
  );
  const hasAwareSelected = selectedDevices.some((d) => DeviceIsSystemType(d, 'Flood'));
  // Map of selected aliases restricted to flood-related devices (AWARE + USGS)
  const selectedFloodDeviceMap = useMemo(() => {
    const map = {};
    floodDeviceIds.forEach((id) => {
      if (selectedDeviceMap?.[id]) {
        map[id] = true;
      }
    });
    return map;
  }, [floodDeviceIds, selectedDeviceMap]);
  const hasSelectedDevice = useMemo(
    () => Object.keys(selectedDeviceMap || {}).some((k) => selectedDeviceMap[k]),
    [selectedDeviceMap],
  );
  const hasDeferredSelectedDevice = useMemo(
    () => Object.keys(deferredSelectedDeviceMap || {}).some((k) => deferredSelectedDeviceMap[k]),
    [deferredSelectedDeviceMap],
  );
  const filterDropdownOptions = getFilterDropdownOptions(
    connectedDevices,
    selectedFloodDeviceMap,
    unitType,
  );
  // include USGS devices alongside AWARE devices in the device dropdown
  // Build option objects with group metadata so the local DropdownSelect can render headers
  const awareDeviceOptions = getAwareDevices(connectedDevices).map((device) => ({
    label: device.alias,
    value: String(device.id),
    group: device.system_type,
  }));
  const usgsDeviceOptions = getUSGSDevices(connectedDevices).map((device) => ({
    label: device.alias || device.monitoring_location_name || device.name,
    value: String(device.id),
    group: device.system_type,
  }));

  const deviceDropdownOptions = [...awareDeviceOptions, ...usgsDeviceOptions];
  const dropdownMenuStyle = {
    maxHeight: '18rem',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
  };

  // time fallback (last 24h)
  const [localRange, setLocalRange] = useState(() => {
    const now = Date.now();
    return {
      start: startTime ?? now - 24 * 60 * 60 * 1000,
      end: endTime ?? now,
    };
  });

  useEffect(() => {
    setLocalRange((prev) => ({
      start: typeof startTime === 'number' ? startTime : prev.start,
      end: typeof endTime === 'number' ? endTime : prev.end,
    }));
  }, [startTime, endTime]);

  const resolvedStartTime = startTime ?? localRange.start;
  const resolvedEndTime = endTime ?? localRange.end;

  useEffect(() => {
    sessionStorage.setItem(
      'filterDropdownOptionSelected',
      JSON.stringify(filterDropdownOptionSelected),
    );
    sessionStorage.setItem('selectedDeviceMap', JSON.stringify(selectedDeviceMap));
  }, [filterDropdownOptionSelected, selectedDeviceMap]);

  useEffect(() => {
    sessionStorage.setItem('selectedDataType', JSON.stringify(selectedDataType));
  }, [selectedDataType]);

  // Ensure `selectedDeviceMap` contains only IDs that exist in `connectedDevices`.
  useEffect(() => {
    if (!connectedDevices || !connectedDevices.length) return;
    const connectedDeviceIdSet = new Set((connectedDevices || []).map((d) => String(d.id)));
    const storedKeys = Object.keys(selectedDeviceMap || {});
    const cleanedKeys = storedKeys.filter(
      (key) => selectedDeviceMap[key] && connectedDeviceIdSet.has(key),
    );
    if (cleanedKeys.length === storedKeys.length) return; // no change
    const newMap = {};
    cleanedKeys.forEach((key) => (newMap[key] = true));
    setSelectedDeviceMap(newMap);
  }, [connectedDevices]);

  // Track which chart type is active and set an appropriate default dataType only
  // when the mode first changes (null → usgs, null → aware, usgs → null, etc.)
  const prevModeRef = useRef(null); // tracks last chart mode ('usgs'|'Flood'|null) to fire the default dataType only on mode transitions
  useEffect(() => {
    const mode = hasUSGSSelected ? 'usgs' : hasAwareSelected ? 'Flood' : null;
    if (mode === prevModeRef.current) return; //Do nothing if the mode hasn't changed

    //Update the ref to the new mode and set the default data type based on the new mode
    prevModeRef.current = mode;
    const newDataType = mode === 'usgs' ? 'Gage height' : mode === 'Flood' ? 'sensordepths' : '';
    setSelectedDataType(newDataType);
    // Sync the DropdownSelector index to match the new default so its label updates.
    const setDropdownTo = mode === 'usgs' ? 'Gage height' : mode === 'Flood' ? 'Actual Depths' : '';
    const newIndex = filterDropdownOptions.findIndex(
      (option) => option && option.props && option.props.value === setDropdownTo,
    );
    setFilterDropdownOptionSelected(newIndex >= 0 ? newIndex : 0);
  }, [hasUSGSSelected, hasAwareSelected, filterDropdownOptions]);

  const fetching = useSelector(selectIntegrationFetching);
  const historyDataLoading = useSelector(selectHistoryDataLoading);
  const initialHistoryLoaded = useSelector(selectInitialHistoryLoaded);
  const reduxSelectedDeviceId = useSelector(selectSelectedDeviceId);
  const linkingGroupId = useSelector(selectLinkingGroupId);
  const { data: allGroups = [] } = useGetGroupsQuery();
  const isChartTransitioning =
    deferredSelectedDeviceMap !== selectedDeviceMap || deferredSelectedDataType !== selectedDataType;

  // Sync graph selection with selectedDeviceID (Only for floodDevices)
  const prevSelectedIdRef = useRef(null);
  useEffect(() => {
    if (widget) return; // Disabled in widget mode
    if (reduxSelectedDeviceId == null) {
      prevSelectedIdRef.current = null; // reset so re-selecting same device fires fresh
      return;
    }
    const id = String(reduxSelectedDeviceId);
    if (prevSelectedIdRef.current === id) return; // already reflected — skip
    if (!floodDeviceIds.includes(id)) return; // no graph for this device type — skip
    prevSelectedIdRef.current = id;
    setSelectedDeviceMap({ [id]: true });
  }, [reduxSelectedDeviceId, floodDeviceIds]);

  // Sync graph selection when a group is selected — show all flood-capable devices in the group.
  const prevLinkingGroupIdRef = useRef(null);
  useEffect(() => {
    if (widget) return;
    if (linkingGroupId == null) {
      prevLinkingGroupIdRef.current = null; // reset so re-selecting same group fires fresh
      return;
    }
    if (prevLinkingGroupIdRef.current === linkingGroupId) return;
    prevLinkingGroupIdRef.current = linkingGroupId;
    const newMap = resolveGroupDeviceMap(linkingGroupId, allGroups, floodDeviceIds);
    if (!newMap) return; // group has no flood-capable devices — leave graph as-is
    setSelectedDeviceMap(newMap);
  }, [linkingGroupId, allGroups, floodDeviceIds, widget]);

  // On mount: if Redux has no device selected use sessionStorage
  useEffect(() => {
    if (widget) return; // Disabled in widget mode
    if (reduxSelectedDeviceId != null) return; // Redux already owns the selection
    const stored = safeJSONParse(sessionStorage.getItem('selectedDeviceMap'), null);
    if (!stored) return;
    const activeIds = Object.keys(stored).filter((k) => stored[k]);
    if (activeIds.length === 0) return;
    const firstId = activeIds[0];
    prevSelectedIdRef.current = firstId; // prevent the sync effect from re-triggering
    dispatch(setSelectedDeviceId(Number(firstId) || firstId));
  }, []);

  const handleSelectedDeviceIndex = useCallback(
    (index) => {
      if (typeof onSelectedDeviceIndex !== 'function') return;
      onSelectedDeviceIndex(index);
    },
    [onSelectedDeviceIndex],
  );

  return (
    <Col className="d-flex flex-column h-100 min-h-0">
      <div className="d-flex flex-column flex-md-row gap-2 gap-md-3 align-items-stretch align-items-md-center">
        <DropdownSelector
          selectedOption={filterDropdownOptionSelected}
          options={filterDropdownOptions}
          setSelected={setFilterDropdownOptionSelected}
          contentStyle={dropdownMenuStyle}
          onChange={(event, item) => {
            setUnitType(false);
            const nextValue = item?.props?.value ?? item?.value ?? item;
            startTransition(() => {
              setSelectedDataType(nextValue);
            });
          }}
        />

        <DropdownSelect
          multiple
          selectedOption={selectedDeviceIds}
          options={deviceDropdownOptions}
          setSelected={(arr) => {
            const map = {};
            (Array.isArray(arr) ? arr : []).forEach((id) => {
              map[String(id)] = true;
            });
            startTransition(() => {
              setSelectedDeviceMap(map);
            });
          }}
          contentStyle={dropdownMenuStyle}
              onClearAll={() => {
                startTransition(() => {
                  setSelectedDeviceMap({});
                  setSelectedDataType('');
                });
                if (!widget) {
                  dispatch(setSelectedDeviceId(null));
                  prevSelectedIdRef.current = null;
            }
          }}
          onChange={(event, item) => {
                const id = String(item);
                const turningOn = !selectedDeviceMap[id];
                startTransition(() => {
                  setSelectedDeviceMap((prev) => ({
                    ...prev,
                    [id]: !prev[id],
                  }));
                });
                if (turningOn && !widget) {
                  prevSelectedIdRef.current = id;
                  dispatch(setSelectedDeviceId(Number(id) || id));
            }
          }}
          isOptionDisabled={(option) => {
            const group = option?.group ?? '';
            if (hasUSGSSelected && group === 'Flood') return true;
            if (hasAwareSelected && group === 'USGS') return true;
            return false;
          }}
        />
      </div>
      <div className="pt-2 pb-0 flex-fill w-100 min-h-0 device-graph-scroll-container position-relative">
        {/* Show loading overlay while initial historical data is being fetched */}
        {fetching && !initialHistoryLoaded && hasSelectedDevice && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 z-1 rounded-3">
            <div className="text-white text-center">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Loading historical data...
            </div>
          </div>
        )}
        {(isChartTransitioning || (historyDataLoading && hasSelectedDevice)) && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 z-2 rounded-3">
            <div className="text-white text-center">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Loading chart data...
            </div>
          </div>
        )}
        {connectedDevicesLoading && connectedDevices.length === 0 ? (
          <div className="bg-primary text-white d-flex align-items-center justify-content-center rounded-3 h-100 w-100">
            <ConnectedDevicesLoadingState message="Loading graph..." />
          </div>
        ) : hasDeferredSelectedDevice ? (
          hasUSGSSelected ? (
            <USGSDeviceGraph
              title={title}
              selectedDeviceMap={deferredSelectedDeviceMap}
              selectedDataType={deferredSelectedDataType}
              unitType={unitType}
              setUnitType={setUnitType}
              startTime={resolvedStartTime}
              endTime={resolvedEndTime}
              onSelectedDeviceIndex={handleSelectedDeviceIndex}
              setShowCurrentDevice={setShowCurrentDevice}
              setShowProvisional={setShowProvisional}
            />
          ) : (
            <AwareDeviceChart
              title={title}
              selectedDeviceMap={deferredSelectedDeviceMap}
              selectedDataType={deferredSelectedDataType}
              unitType={unitType}
              setUnitType={setUnitType}
              startTime={resolvedStartTime}
              endTime={resolvedEndTime}
              onSelectDeviceIndex={(index) => {
                handleSelectedDeviceIndex(index);
                setShowCurrentDevice?.(true);
              }}
            />
          )
        ) : (
          <div className="bg-primary text-white d-flex align-items-center justify-content-center rounded-3 h-100 w-100">
            <div className="fw-semibold">Select a device</div>
          </div>
        )}
      </div>
    </Col>
  );
};

export default DeviceGraph;
